import React, { useEffect, useMemo, useState } from 'react';
import { MenuItem, Stack, TextField, Typography } from '@mui/material';
import { Button, FormSection } from '../../../../../components/ui';
import { useFeatureTheme } from '../../../../../theme/hooks/useFeatureTheme';
import { FORM_STUDIO_COPY } from '../../constants/formStudioConfig';
import useStatusTriggers from '../../hooks/useStatusTriggers';
import {
  getStatusFields,
  getStatusRowId,
  getSubStatusFieldsForParent,
  parentStatusFieldHasOptions,
} from '../../utils/statusTriggerUtils';
import StatusOptionTriggersAccordion from './StatusOptionTriggersAccordion';

function SaveFirstNotice({ message }) {
  return <Typography color="text.secondary">{message}</Typography>;
}

export default function StatusTriggersSection({
  formId,
  workspaceId,
  hasFormId,
  formFields = [],
  onGoToFields,
}) {
  const { semantic, typographyStyles } = useFeatureTheme();
  const copy = FORM_STUDIO_COPY.automations.statusTriggers;
  const saveCopy = FORM_STUDIO_COPY.automations;

  const statusFields = useMemo(() => getStatusFields(formFields), [formFields]);
  const [selectedParentKey, setSelectedParentKey] = useState(
    statusFields[0]?.key_name || ''
  );
  const [expandedOption, setExpandedOption] = useState('');

  useEffect(() => {
    if (!statusFields.length) {
      setSelectedParentKey('');
      return;
    }
    const stillValid = statusFields.some((field) => field.key_name === selectedParentKey);
    if (!stillValid) {
      setSelectedParentKey(statusFields[0].key_name);
      setExpandedOption('');
    }
  }, [selectedParentKey, statusFields]);

  const selectedParent = useMemo(
    () => statusFields.find((field) => field.key_name === selectedParentKey) || null,
    [selectedParentKey, statusFields]
  );

  const subStatusFields = useMemo(
    () => getSubStatusFieldsForParent(formFields, selectedParentKey),
    [formFields, selectedParentKey]
  );

  const triggers = useStatusTriggers({
    formId,
    workspaceId,
    parentKey: selectedParentKey,
    enabled: hasFormId && Boolean(selectedParentKey),
  });

  const statusRows = triggers.statuses;

  if (!hasFormId) {
    return (
      <FormSection title={copy.title} description={copy.caption}>
        <SaveFirstNotice message={saveCopy.saveFirst} />
      </FormSection>
    );
  }

  if (!statusFields.length) {
    return (
      <FormSection title={copy.title} description={copy.caption}>
        <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary, mb: 1.5 }}>
          {copy.noStatusField}
        </Typography>
        <Button variant="outline" onClick={onGoToFields}>
          {copy.goToFields}
        </Button>
      </FormSection>
    );
  }

  const parentHasOptions = parentStatusFieldHasOptions(selectedParent);

  return (
    <FormSection title={copy.title} description={copy.caption}>
      <Stack spacing={2}>
        <TextField
          select
          label={copy.parentFieldLabel}
          size="small"
          fullWidth
          value={selectedParentKey}
          onChange={(event) => {
            setSelectedParentKey(event.target.value);
            setExpandedOption('');
          }}
        >
          {statusFields.map((field) => (
            <MenuItem key={`${field.item_id}-${field.key_name}`} value={field.key_name}>
              {field.alias_name || field.key_name}
            </MenuItem>
          ))}
        </TextField>

        {!parentHasOptions ? (
          <>
            <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary }}>
              {copy.noStatusOptions}
            </Typography>
            <Button variant="outline" onClick={onGoToFields}>
              {copy.goToFields}
            </Button>
          </>
        ) : null}

        {triggers.isLoading ? (
          <Typography sx={{ color: semantic.text.muted }}>{copy.loading}</Typography>
        ) : null}
        {triggers.loadError ? (
          <Typography sx={{ color: semantic.states.danger }}>{triggers.loadError}</Typography>
        ) : null}

        {parentHasOptions && statusRows.length === 0 && !triggers.isLoading ? (
          <Typography sx={{ ...typographyStyles.caption, color: semantic.text.secondary }}>
            {copy.syncHint}
          </Typography>
        ) : null}

        {parentHasOptions
          ? statusRows.map((statusRow) => {
              const optionName = statusRow.status_name;
              return (
                <StatusOptionTriggersAccordion
                  key={getStatusRowId(statusRow) || optionName}
                  statusRow={statusRow}
                  statusOptionName={optionName}
                  subStatusFields={subStatusFields}
                  expanded={expandedOption === optionName}
                  workspaceId={workspaceId}
                  isSaving={triggers.isSaving}
                  onToggle={(name, isExpanded) =>
                    setExpandedOption(isExpanded ? name : '')
                  }
                  onSaveStatusTriggers={(row, nextTriggers) =>
                    triggers.updateStatusTriggers(row, nextTriggers)
                  }
                  onSaveSubstatusTriggers={(row, subName, subKey, nextTriggers) =>
                    triggers.updateSubstatusTriggers(row, subName, subKey, nextTriggers)
                  }
                />
              );
            })
          : null}
      </Stack>
    </FormSection>
  );
}
